import { ReactElement, useCallback, useMemo, useState } from 'react'
import {
    StyledChat,
    StyledMessageContent,
    ChatIcon,
    StyledThread,
} from './styles'
import { ChatInput, Grid, SVG, SVGs, Text } from '../..'
import { ChatProps } from './props'
import InfiniteScroll from 'react-infinite-scroll-component'
import { theme } from '../../Styles/stitches.config'

const Chat = ({
    messages,
    buttonText,
    nowLoadingText = 'Loading...',
    quickResponse,
    ...props
}: ChatProps): ReactElement => {
    const [totalMessages, setTotalMessages] = useState(25)

    const shownMessages = useMemo(
        () => messages.slice(0, totalMessages),
        [messages, totalMessages]
    )

    const [messagesHasMore, setMessagesHasMore] = useState(
        shownMessages.length !== messages.length
    )

    const loadMore = useCallback(() => {
        if (shownMessages.length === messages.length) {
            setMessagesHasMore(false)
            return
        }

        setTotalMessages(totalMessages + 25)
    }, [messages.length, shownMessages.length, totalMessages])

    const urlify = useCallback((text: string, color: string) => {
        const urlRegex =
            /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi
        return text.replace(urlRegex, (url: string) => {
            const urlString = url.toString()
            return `<a style="color:${color}";" target="_blank" rel="noopener" href="${urlString}">${urlString}</a>`
        })
    }, [])

    const messageThread = useMemo(
        () =>
            shownMessages.map((msg, key) => {
                const {
                    icon = (
                        <SVG
                            src={SVGs.User}
                            css={{
                                color: '$primary600',
                            }}
                        />
                    ),
                    message,
                    showIcon,
                    timeStamp,
                    user,
                } = msg

                const userIcon =
                    user !== 'sender' ? (
                        <ChatIcon>{showIcon && icon}</ChatIcon>
                    ) : (
                        false
                    )

                return (
                    <Grid gap={4} key={key}>
                        {timeStamp && (
                            <Text
                                css={{
                                    color: '$neutral400',
                                    justifySelf: 'center',
                                }}
                                size="s"
                            >
                                {timeStamp}
                            </Text>
                        )}
                        <StyledMessageContent user={user}>
                            {userIcon}
                            <Text
                                dangerouslySetInnerHTML={{
                                    __html: urlify(
                                        message,
                                        user === 'receiver'
                                            ? theme.colors.primary600.value
                                            : theme.colors.white.value
                                    ),
                                }}
                                primary={user === 'receiver'}
                            />
                        </StyledMessageContent>
                    </Grid>
                )
            }),
        [shownMessages, urlify]
    )

    return (
        <StyledChat>
            <StyledThread id="rr-chat">
                <InfiniteScroll
                    dataLength={shownMessages.length}
                    hasMore={messagesHasMore}
                    inverse
                    loader={nowLoadingText}
                    next={loadMore}
                    scrollableTarget="rr-chat"
                    scrollThreshold={250}
                >
                    {messageThread}
                </InfiniteScroll>
            </StyledThread>
            <ChatInput
                {...props}
                buttonText={buttonText}
                quickResponse={quickResponse}
            />
        </StyledChat>
    )
}

export default Chat

export { StyledChat }
